import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './lib/AuthContext';
import LoginPage from './pages/LoginPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import SitesPage from './pages/SitesPage';
import AgentsPage from './pages/AgentsPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import HomePage from './pages/HomePage';
import CompliancePage from './pages/CompliancePage';
import AuditPage from './pages/AuditPage';
import MainLayout from './components/MainLayout';
import { db } from './lib/firebase';
import { doc, updateDoc, collection, onSnapshot } from 'firebase/firestore';
import './index.css';

// No industry based blocking logic in frontend anymore. We check the legalBlocked claim.

function AppRoutes() {
  const { user, profile, claims, loading } = useAuth();
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'profile', 'admin'
  const [dashboardTab, setDashboardTab] = useState('analytics');
  const [unviewedCount, setUnviewedCount] = useState(0);
  const [selectedSite, setSelectedSite] = useState(null);
  const [onlineAgents, setOnlineAgents] = useState([]);
  const [showAgentsModal, setShowAgentsModal] = useState(false);
  const [initialIssuesFilter, setInitialIssuesFilter] = useState(null); // { site: string, ruleId: string }
  const [initialUrl, setInitialUrl] = useState(null);

  // Unified public routing
  const getPublicRoute = () => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'compliance') return 'compliance';
    if (hash === 'audit') return 'audit';
    if (!hash || hash === 'main' || hash === 'home') return 'home';
    return 'home';
  };

  const [publicView, setPublicView] = useState(getPublicRoute());
  const [showApp, setShowApp] = useState(false);

  // Sync hash with publicView
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (['home', 'compliance', 'audit', 'main', ''].includes(hash)) {
        setPublicView(getPublicRoute());
        setShowApp(false);
      } else if (hash === 'app') {
        setShowApp(true);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Sync with profile on initial load / change
  useEffect(() => {
    if (!profile?.preferences?.defaultViewMode) return;
    const nextMode = profile.preferences.defaultViewMode;
    const frame = requestAnimationFrame(() => {
      setDashboardTab((current) => current === nextMode ? current : nextMode);
    });
    return () => cancelAnimationFrame(frame);
  }, [profile]);

  // Real-time listener on agent heartbeats for status banner
  useEffect(() => {
    if (!user) return;

    const agentsRef = collection(db, 'users', user.uid, 'agents');
    const unsubscribe = onSnapshot(agentsRef, (snapshot) => {
      const now = Date.now();
      const online = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(agent => {
        if (!agent.lastSeen || !agent.online) return false;
        const lastSeen = agent.lastSeen.toDate?.() || new Date(agent.lastSeen.seconds * 1000);
        return (now - lastSeen.getTime()) < 90000;
      });
      setOnlineAgents(online);
    }, (err) => {
      console.error("[App] Agents listener error:", err);
    });

    return () => unsubscribe();
  }, [user]);

  // Persist to profile
  useEffect(() => {
    if (user && dashboardTab && dashboardTab !== profile?.preferences?.defaultViewMode) {
      updateDoc(doc(db, 'users', user.uid), {
        'preferences.defaultViewMode': dashboardTab
      }).catch(err => console.error("Failed to save tab preference:", err));
    }
  }, [dashboardTab, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-pearl">
        <div className="flex flex-col items-center gap-6">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 border-4 border-teal/10 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-teal border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-espresso font-bold uppercase tracking-widest animate-pulse opacity-50">Loading Scout...</p>
        </div>
      </div>
    );
  }

  // Handle Public Views
  if (!showApp) {
    const goToAudit = () => {
      window.location.hash = 'audit';
    };

    let publicContent = <HomePage onAuditClick={goToAudit} />;
    if (publicView === 'compliance') publicContent = <CompliancePage onAuditClick={goToAudit} />;
    if (publicView === 'audit') publicContent = <AuditPage onAuditClick={goToAudit} />;

    return (
      <div className="min-h-screen flex flex-col">
        {/* Simple Header for Public Pages */}
        <header className="bg-warm-pearl border-b border-espresso/5 py-6 px-8 flex justify-between items-center sticky top-0 z-50">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.location.hash = 'home'}>
            <img src="/logo.png" className="w-8 h-8" alt="Accessibility Scout Logo" />
            <span className="text-espresso font-black uppercase tracking-tighter">Accessibility Scout</span>
          </div>
          <nav className="hidden md:flex gap-4">
            <button onClick={() => window.location.hash = 'home'} className={`nav-link ${publicView === 'home' ? 'active' : ''}`}>Home</button>
            <button onClick={() => window.location.hash = 'compliance'} className={`nav-link ${publicView === 'compliance' ? 'active' : ''}`}>Compliance</button>
            <button onClick={() => window.location.hash = 'audit'} className={`nav-link ${publicView === 'audit' ? 'active' : ''}`}>Direct Audit</button>
          </nav>
          <div className="flex gap-4">
            {user ? (
              <button onClick={() => window.location.hash = 'app'} className="pill-button !py-2 !px-6">Go to Dashboard</button>
            ) : (
              <button onClick={() => window.location.hash = 'app'} className="pill-button !py-2 !px-6">Sign In</button>
            )}
          </div>
        </header>
        <main className="flex-1">
          {publicContent}
        </main>
      </div>
    );
  }

  // APP VIEW (Requires Auth)
  if (!user) {
    return <LoginPage />;
  }

  // Logged in but hasn't completed onboarding → Onboarding
  if (!profile || !profile.onboardingComplete) {
    return <OnboardingPage />;
  }

  // Check custom claims: block legal professionals who did not verify
  if (claims?.legalBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-pearl p-4">
        <div className="bento-card border-fail max-w-md text-center">
          <span className="text-5xl mb-8 block">⚖️</span>
          <h2 className="text-2xl font-black text-fail mb-4 uppercase tracking-widest">Access Suspended</h2>
          <p className="text-espresso opacity-70 mb-8 leading-relaxed">
            Your account requires manual verification.
            Our service is designed strictly for business owners and builders defending against litigation.
            Please contact support to proceed.
          </p>
          <button onClick={() => window.location.hash = 'home'} className="pill-button">Return Home</button>
        </div>
      </div>
    );
  }

  // Define content based on currentView
  let content = null;
  if (currentView === 'profile') {
    content = <ProfilePage onViewChange={setCurrentView} />;
  } else if (currentView === 'sites') {
    content = (
      <SitesPage
        onViewChange={setCurrentView}
        initialSite={selectedSite}
        onIssueSelect={(site, ruleId) => {
          setInitialIssuesFilter({ site, ruleId });
          setCurrentView('dashboard');
          setDashboardTab('issues');
        }}
        onRescan={(domain) => {
          setInitialUrl(domain);
          setCurrentView('dashboard');
          setDashboardTab('new-scan');
        }}
      />
    );
  } else if (currentView === 'agents') {
    content = <AgentsPage onViewChange={setCurrentView} onlineAgents={onlineAgents} />;
  } else if (currentView === 'admin' && profile?.role === 'admin') {
    content = <AdminDashboardPage onViewChange={setCurrentView} />;
  } else {
    content = (
      <DashboardPage
        onViewChange={setCurrentView}
        externalTab={dashboardTab}
        setExternalTab={setDashboardTab}
        setUnviewedCount={setUnviewedCount}
        onlineAgents={onlineAgents}
        initialIssuesFilter={initialIssuesFilter}
        onClearInitialFilter={() => setInitialIssuesFilter(null)}
        initialUrl={initialUrl}
        onClearInitialUrl={() => setInitialUrl(null)}
        onSiteSelect={(site) => {
          setSelectedSite(site);
          setCurrentView('sites');
        }}
      />
    );
  }

  return (
    <MainLayout
      currentView={currentView}
      onViewChange={setCurrentView}
      dashboardTab={dashboardTab}
      setDashboardTab={setDashboardTab}
      unviewedCount={unviewedCount}
      agentCount={onlineAgents.length}
    >
      {content}
    </MainLayout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
